import React, { useState, useEffect } from 'react';
import { Video, Phone, ShieldCheck, Sparkles, Users, Star, ArrowRight, ChevronDown, Lock, Camera, Zap, Globe, Heart } from 'lucide-react';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.avente.partner';

const DakhlaApp = () => {
  const [isVisible, setIsVisible] = useState({});
  const [activeFaq, setActiveFaq] = useState(null);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const openPlayStore = () => {
    window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
  };

  const faqs = [
    { question: "What is Dakhla Video Call App?", answer: "Dakhla is a high-quality live video calling application designed for real-time 1-on-1 video connections, meeting new friends, and instant face-to-face conversations." },
    { question: "Is Dakhla safe and secure?", answer: "Yes! Dakhla features strict user verification, end-to-end encryption, and built-in safety controls so you can enjoy video calling with total peace of mind." },
    { question: "Are profiles on Dakhla verified?", answer: "We enforce mandatory photo & account verification to protect our community against fake profiles and catfishing." },
    { question: "Is Dakhla free to download?", answer: "Dakhla is completely free to download on Google Play with instant access to basic video calls and partner matching." }
  ];

  return (
    <div className="min-h-screen bg-slate-950 font-sans text-slate-100 overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/80 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => scrollToSection('home')}>
            <div className="w-10 h-10 bg-gradient-to-tr from-rose-500 via-purple-600 to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg shadow-rose-500/25 transform rotate-3">
              <Video className="text-white w-5 h-5" />
            </div>
            <div>
              <span className="text-xl sm:text-2xl font-black tracking-tight text-white">
                Dakhla
              </span>
              <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/30">
                Video Call App
              </span>
            </div>
          </div>

          <nav className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('home')} className="font-semibold text-slate-300 hover:text-rose-400 transition-colors">Home</button>
            <button onClick={() => scrollToSection('features')} className="font-semibold text-slate-300 hover:text-rose-400 transition-colors">Features</button>
            <button onClick={() => scrollToSection('how-it-works')} className="font-semibold text-slate-300 hover:text-rose-400 transition-colors">How it Works</button>
            <button onClick={openPlayStore} className="bg-gradient-to-r from-rose-500 to-indigo-600 text-white px-6 py-2.5 rounded-full font-bold hover:brightness-110 transition-all shadow-lg shadow-rose-500/25">
              Get App
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 lg:pt-40 lg:pb-32 px-4 relative overflow-hidden" data-animate>
        {/* Background Glowing Gradients */}
        <div className="absolute top-0 right-1/4 -z-10 w-[600px] h-[600px] bg-rose-600/20 rounded-full blur-[140px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/4 -z-10 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[140px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div className={`space-y-8 transition-all duration-1000 ${isVisible['home'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-flex items-center px-4 py-2 rounded-full border border-rose-500/30 bg-rose-500/10 backdrop-blur-md">
              <Sparkles className="w-4 h-4 text-rose-400 mr-2" />
              <span className="text-sm font-bold text-rose-300">Live 1-on-1 HD Video Calling App</span>
            </div>
            
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black leading-[1.1] tracking-tight text-white">
              Connect via <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 via-fuchsia-400 to-indigo-400">Live Video</span> Anytime, Anywhere.
            </h1>
            
            <p className="text-base sm:text-xl text-slate-300 leading-relaxed max-w-lg">
              Experience seamless, high-definition video calls with real people. Match instantly, talk face-to-face, and build genuine connections on Dakhla.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={openPlayStore}
                className="bg-gradient-to-r from-rose-500 via-purple-600 to-indigo-600 hover:brightness-110 text-white px-8 py-4 rounded-full font-bold text-lg shadow-xl shadow-rose-500/30 transform hover:-translate-y-1 transition-all flex items-center justify-center cursor-pointer"
              >
                Download on Google Play
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center space-x-6 pt-4 border-t border-slate-800/80">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <img key={i} className="w-11 h-11 rounded-full border-2 border-slate-900 shadow-sm object-cover" src={`https://randomuser.me/api/portraits/${i % 2 === 0 ? 'women' : 'men'}/${30 + i}.jpg`} alt="User" />
                ))}
              </div>
              <div className="text-sm">
                <div className="flex text-amber-400 mb-1">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                </div>
                <p className="font-bold text-slate-200">4.8/5 Rating on Google Play</p>
              </div>
            </div>
          </div>

          {/* Interactive Mockup */}
          <div className={`relative transition-all duration-1000 delay-300 ${isVisible['home'] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative mx-auto w-72 md:w-80 h-[600px] md:h-[650px] bg-slate-900 rounded-[3rem] border-8 border-slate-800 shadow-2xl overflow-hidden ring-1 ring-slate-700/50">
              {/* App UI Mockup inside Phone */}
              <div className="w-full h-full bg-slate-950 relative flex flex-col overflow-hidden">
                {/* Remote Video Stream (Main Feed) */}
                <div className="absolute inset-0 bg-slate-900">
                  <img className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=800&q=80" alt="Video Call Partner" />
                  <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-transparent to-slate-950/80"></div>
                </div>

                {/* Top Call Info */}
                <div className="relative p-4 pt-10 flex justify-between items-center z-10">
                  <div className="flex items-center gap-2 bg-slate-900/80 backdrop-blur-md px-3 py-1.5 rounded-full border border-slate-700/50">
                    <span className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse"></span>
                    <span className="text-xs font-bold text-white">LIVE 04:12</span>
                  </div>
                  <div className="bg-slate-900/80 backdrop-blur-md px-3 py-1.5 rounded-full border border-slate-700/50 text-xs font-semibold text-rose-300">
                    Anya, 23
                  </div>
                </div>

                {/* Floating Self Camera View (Picture in Picture) */}
                <div className="absolute top-20 right-4 w-24 h-36 bg-slate-950 rounded-2xl border-2 border-slate-700/80 shadow-2xl overflow-hidden z-20">
                  <img className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&w=400&q=80" alt="Your Camera" />
                  <div className="absolute bottom-1 right-1 bg-slate-900/80 p-1 rounded-md">
                    <Camera className="w-3 h-3 text-white" />
                  </div>
                </div>

                {/* Floating Heart animation mockup */}
                <div className="absolute bottom-28 left-6 z-20 flex flex-col gap-2">
                  <div className="bg-rose-500/20 backdrop-blur-md border border-rose-500/30 px-3 py-1 rounded-full text-xs font-medium text-rose-200 flex items-center gap-1.5 animate-bounce">
                    <Heart className="w-3.5 h-3.5 text-rose-400 fill-current" /> Liked your video!
                  </div>
                </div>

                {/* Bottom Call Controls */}
                <div className="relative mt-auto p-6 flex justify-around items-center z-10 bg-slate-950/60 backdrop-blur-md border-t border-slate-800/60">
                  <button className="w-12 h-12 bg-slate-800/80 text-white rounded-full flex items-center justify-center border border-slate-700 hover:bg-slate-700 transition-colors">
                    <Camera className="w-5 h-5" />
                  </button>
                  <button onClick={openPlayStore} className="w-14 h-14 bg-rose-600 text-white rounded-full shadow-lg shadow-rose-600/50 flex items-center justify-center transform hover:scale-105 transition-transform">
                    <Phone className="w-6 h-6 rotate-[135deg] fill-current" />
                  </button>
                  <button className="w-12 h-12 bg-slate-800/80 text-white rounded-full flex items-center justify-center border border-slate-700 hover:bg-slate-700 transition-colors">
                    <Zap className="w-5 h-5 text-amber-400" />
                  </button>
                </div>
              </div>
            </div>

            {/* Floating Badges */}
            <div className="absolute top-16 -left-6 md:-left-10 bg-slate-900/90 backdrop-blur-xl px-5 py-3 rounded-2xl shadow-2xl border border-slate-800 animate-bounce" style={{ animationDuration: '3.5s' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center border border-emerald-500/30">
                  <Video className="text-emerald-400 w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-white">Instant HD Call</div>
                  <div className="text-xs text-emerald-400">Connected in 1s</div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-28 -right-6 md:-right-10 bg-slate-900/90 backdrop-blur-xl px-5 py-3 rounded-2xl shadow-2xl border border-slate-800 animate-bounce" style={{ animationDuration: '4.5s' }}>
               <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-rose-500/20 rounded-xl flex items-center justify-center border border-rose-500/30">
                  <ShieldCheck className="text-rose-400 w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-white">100% Verified</div>
                  <div className="text-xs text-slate-400">Anti-fake protection</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-slate-900/50 border-y border-slate-800/80" data-animate>
        <div className="max-w-7xl mx-auto px-4">
          <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${isVisible['features'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-3xl md:text-5xl font-black mb-6 text-white">Why Choose Dakhla?</h2>
            <p className="text-lg text-slate-400">Designed for smooth video calling, high privacy, and real-time interaction with amazing partners.</p>
          </div>

          <div className={`grid md:grid-cols-3 gap-8 transition-all duration-1000 delay-300 ${isVisible['features'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="bg-slate-900 p-8 rounded-3xl hover:border-rose-500/50 transition-all duration-300 border border-slate-800 shadow-xl group">
              <div className="w-14 h-14 bg-rose-500/20 text-rose-400 rounded-2xl flex items-center justify-center mb-6 border border-rose-500/30 group-hover:scale-110 transition-transform">
                <Video className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">High Definition Video</h3>
              <p className="text-slate-400 leading-relaxed">Enjoy smooth, lag-free 1-on-1 video calls optimized for mobile devices even on weak network connections.</p>
            </div>
            
            <div className="bg-slate-900 p-8 rounded-3xl hover:border-indigo-500/50 transition-all duration-300 border border-slate-800 shadow-xl group">
              <div className="w-14 h-14 bg-indigo-500/20 text-indigo-400 rounded-2xl flex items-center justify-center mb-6 border border-indigo-500/30 group-hover:scale-110 transition-transform">
                <ShieldCheck className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Verified Users Only</h3>
              <p className="text-slate-400 leading-relaxed">Strict authentication & selfie verification ensure you communicate with genuine people every time.</p>
            </div>

            <div className="bg-slate-900 p-8 rounded-3xl hover:border-purple-500/50 transition-all duration-300 border border-slate-800 shadow-xl group">
              <div className="w-14 h-14 bg-purple-500/20 text-purple-400 rounded-2xl flex items-center justify-center mb-6 border border-purple-500/30 group-hover:scale-110 transition-transform">
                <Lock className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Private & Encrypted</h3>
              <p className="text-slate-400 leading-relaxed">Your privacy is our priority. All video calls are encrypted, protecting your personal data and conversations.</p>
            </div>

            <div className="bg-slate-900 p-8 rounded-3xl hover:border-amber-500/50 transition-all duration-300 border border-slate-800 shadow-xl group">
              <div className="w-14 h-14 bg-amber-500/20 text-amber-400 rounded-2xl flex items-center justify-center mb-6 border border-amber-500/30 group-hover:scale-110 transition-transform">
                <Globe className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Global Partner Discovery</h3>
              <p className="text-slate-400 leading-relaxed">Meet people around the world or filter by regional language preferences for effortless communication.</p>
            </div>

            <div className="bg-slate-900 p-8 rounded-3xl hover:border-pink-500/50 transition-all duration-300 border border-slate-800 shadow-xl group">
              <div className="w-14 h-14 bg-pink-500/20 text-pink-400 rounded-2xl flex items-center justify-center mb-6 border border-pink-500/30 group-hover:scale-110 transition-transform">
                <Zap className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Instant Connection</h3>
              <p className="text-slate-400 leading-relaxed">No waiting! Start a video call with active partners with a single tap and enjoy instant interactions.</p>
            </div>

            <div className="bg-slate-900 p-8 rounded-3xl hover:border-emerald-500/50 transition-all duration-300 border border-slate-800 shadow-xl group">
              <div className="w-14 h-14 bg-emerald-500/20 text-emerald-400 rounded-2xl flex items-center justify-center mb-6 border border-emerald-500/30 group-hover:scale-110 transition-transform">
                <Users className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Vibrant Community</h3>
              <p className="text-slate-400 leading-relaxed">Join a friendly community of users who love sharing live moments, conversations, and laughs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-slate-950" data-animate>
         <div className="max-w-7xl mx-auto px-4">
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible['how-it-works'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-3xl md:text-5xl font-black mb-6 text-white">How Dakhla Works</h2>
            <p className="text-slate-400 text-lg max-w-xl mx-auto">Get started with live video calling in just four quick steps.</p>
          </div>

          <div className={`grid md:grid-cols-4 gap-8 relative transition-all duration-1000 delay-300 ${isVisible['how-it-works'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-12 left-[10%] right-[10%] h-1 bg-gradient-to-r from-rose-500/40 via-purple-500/40 to-indigo-500/40"></div>

            <div className="relative text-center">
              <div className="w-24 h-24 mx-auto bg-slate-900 border-4 border-rose-500 rounded-full flex items-center justify-center shadow-xl shadow-rose-500/20 mb-6 z-10 relative">
                <span className="text-3xl font-black text-rose-400">1</span>
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Download App</h3>
              <p className="text-slate-400 text-sm">Install Dakhla Video call app directly from Google Play Store.</p>
            </div>

            <div className="relative text-center">
              <div className="w-24 h-24 mx-auto bg-slate-900 border-4 border-purple-500 rounded-full flex items-center justify-center shadow-xl shadow-purple-500/20 mb-6 z-10 relative">
                <span className="text-3xl font-black text-purple-400">2</span>
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Quick Profile Setup</h3>
              <p className="text-slate-400 text-sm">Add your avatar and set your video matching preferences.</p>
            </div>

            <div className="relative text-center">
              <div className="w-24 h-24 mx-auto bg-slate-900 border-4 border-indigo-500 rounded-full flex items-center justify-center shadow-xl shadow-indigo-500/20 mb-6 z-10 relative">
                <span className="text-3xl font-black text-indigo-400">3</span>
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Tap to Video Call</h3>
              <p className="text-slate-400 text-sm">Tap call to instantly connect face-to-face with live partners.</p>
            </div>

            <div className="relative text-center">
              <div className="w-24 h-24 mx-auto bg-slate-900 border-4 border-pink-500 rounded-full flex items-center justify-center shadow-xl shadow-pink-500/20 mb-6 z-10 relative">
                <span className="text-3xl font-black text-pink-400">4</span>
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">Make Friends</h3>
              <p className="text-slate-400 text-sm">Build real relationships through enjoyable video conversations.</p>
            </div>
          </div>
         </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-rose-950/60 via-slate-900 to-indigo-950/60 text-white relative overflow-hidden border-t border-slate-800" data-animate id="cta">
        <div className={`max-w-4xl mx-auto px-4 text-center transition-all duration-1000 ${isVisible['cta'] ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <Video className="w-16 h-16 mx-auto text-rose-400 mb-8 animate-pulse" />
          <h2 className="text-4xl md:text-5xl font-black mb-8">Start Live Video Calling Now</h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Join thousands of users having live video chats every minute. Download Dakhla Video Call App today!
          </p>
          <button 
            onClick={openPlayStore}
            className="bg-white text-slate-950 hover:bg-slate-100 px-8 py-4 rounded-full font-bold text-lg shadow-xl shadow-white/10 transform hover:-translate-y-1 transition-all inline-flex items-center cursor-pointer"
          >
            Get Dakhla on Google Play
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-900/50" data-animate id="faq">
        <div className="max-w-3xl mx-auto px-4">
          <div className={`text-center mb-12 transition-all duration-1000 ${isVisible['faq'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-3xl md:text-4xl font-black mb-4 text-white">Frequently Asked Questions</h2>
          </div>

          <div className={`space-y-4 transition-all duration-1000 delay-300 ${isVisible['faq'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {faqs.map((faq, index) => (
              <div key={index} className="border border-slate-800 rounded-2xl overflow-hidden bg-slate-900">
                <button
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  className="w-full p-6 text-left flex items-center justify-between focus:outline-none"
                >
                  <span className="font-bold text-lg text-white">{faq.question}</span>
                  <ChevronDown className={`transform transition-transform text-slate-400 ${activeFaq === index ? 'rotate-180 text-rose-400' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${activeFaq === index ? 'max-h-48' : 'max-h-0'}`}>
                  <div className="p-6 pt-0 text-slate-400 leading-relaxed border-t border-slate-800/50">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
               <div className="w-8 h-8 bg-gradient-to-tr from-rose-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <Video className="text-white w-4 h-4" />
              </div>
              <span className="text-xl font-bold text-white">Dakhla Video Call App</span>
            </div>
            <div className="flex space-x-6">
              <a href="/terms-and-conditions" className="text-slate-400 hover:text-rose-400 font-medium">Terms</a>
              <a href="/privacy-policy" className="text-slate-400 hover:text-rose-400 font-medium">Privacy</a>
              <a href="/contact-us" className="text-slate-400 hover:text-rose-400 font-medium">Contact</a>
            </div>
          </div>
          <div className="text-center md:text-left text-slate-500 text-sm">
            © {new Date().getFullYear()} Dakhla App (AVENTE MATNITCH PRIVATE LIMITED). All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DakhlaApp;
